import { MouseEvent } from 'react';
import { SUBMODULE_TYPE, SUBMODULE_VIEW } from '@constants/constants';
import { buildSubmoduleUrl } from '@utils/common';
import { ADMIN_ONLY_SUBMODULES, NAVIGATION_CONFIG } from './navConfig';
import { NavGroup, NavModule, NavSubmodule, NavTree, SideNavOptions } from './types';

type IsCurrentRoute = (path: string | string[]) => boolean;

/**
 * Flattens the modules API options into one list of submodules.
 * URLs keep the backend module name, so existing routes and deep links still work.
 * A submodule that is the only one in its backend module takes the module's name.
 * TAB-view modules render their submodules as tabs, so they stay a single submodule.
 */
export const getApiSubmodules = (options: SideNavOptions[]): NavSubmodule[] =>
  options.flatMap((option) => {
    const subOptions = option.subOptions ?? [];

    if (subOptions.length > 1 && option.metadata?.submoduleView === SUBMODULE_VIEW.TAB) {
      return [
        {
          type: subOptions[0].type ?? '',
          label: option.label,
          href: buildSubmoduleUrl(option.label, subOptions[0].path),
          match: subOptions.map((sub) => sub.path),
          moduleLabel: option.label,
        },
      ];
    }

    return subOptions.map((sub) => ({
      type: sub.type ?? '',
      label: subOptions.length === 1 ? option.label : sub.label,
      href: buildSubmoduleUrl(option.label, sub.path),
      match: [sub.path],
      moduleLabel: option.label,
    }));
  });

// Converts the admin-only frontend pages into the sidebar submodule format
export const getAdminOnlySubmodules = (): NavSubmodule[] =>
  ADMIN_ONLY_SUBMODULES.map((item) => ({
    type: item.type,
    label: item.label,
    href: item.path,
    match: item.path,
    moduleLabel: item.label,
  }));

// Demo users get a fixed set of modules that includes the demo Getting Started page
export const isDemoNavigation = (submodules: NavSubmodule[]): boolean =>
  submodules.some((submodule) => submodule.type === SUBMODULE_TYPE.GETTING_STARTED_DEMO);

// Wraps a submodule as a module of its own, so it renders as a direct link
export const toStandaloneModule = (submodule: NavSubmodule): NavModule => ({
  key: submodule.href,
  nested: false,
  label: submodule.label,
  submodules: [submodule],
});

/**
 * Keeps the backend module structure for submodules shown outside the groups:
 * a module with one such submodule is a direct link, a module with several is nested.
 */
export const buildUngroupedModules = (submodules: NavSubmodule[]): NavModule[] => {
  const submodulesByModule = new Map<string, NavSubmodule[]>();
  submodules.forEach((submodule) =>
    submodulesByModule.set(submodule.moduleLabel, [
      ...(submodulesByModule.get(submodule.moduleLabel) ?? []),
      submodule,
    ]),
  );

  return Array.from(submodulesByModule, ([moduleLabel, moduleSubmodules]) =>
    moduleSubmodules.length === 1
      ? toStandaloneModule(moduleSubmodules[0])
      : {
          key: `ungrouped-${moduleLabel}`,
          nested: true,
          label: moduleLabel,
          submodules: moduleSubmodules,
        },
  );
};

/**
 * Builds the group > module > submodule tree from NAVIGATION_CONFIG using the given submodules.
 * Submodules whose type is not in NAVIGATION_CONFIG are returned as ungrouped modules.
 */
export const buildNavTree = (submodules: NavSubmodule[]): NavTree => {
  const submodulesByType = new Map<string, NavSubmodule[]>();
  submodules.forEach((submodule) =>
    submodulesByType.set(submodule.type, [
      ...(submodulesByType.get(submodule.type) ?? []),
      submodule,
    ]),
  );

  const groups = NAVIGATION_CONFIG.map(({ groupType, label, icon, modules }) => ({
    key: groupType,
    label,
    icon,
    modules: modules.flatMap((module, index): NavModule[] => {
      const moduleSubmodules = module.submoduleTypes.flatMap(
        (type) => submodulesByType.get(type) ?? [],
      );
      if (moduleSubmodules.length === 0) return [];

      if (module.nested) {
        return [
          {
            key: `${groupType}-${index}`,
            nested: true,
            // Falls back to the first submodule's name when the config has no label
            label: module.label ?? moduleSubmodules[0].label,
            submodules: moduleSubmodules,
          },
        ];
      }

      // Not nested: each submodule sits directly under the group
      return moduleSubmodules.map((submodule) =>
        toStandaloneModule({ ...submodule, label: module.label ?? submodule.label }),
      );
    }),
  })).filter((group) => group.modules.length > 0);

  const groupedTypes = new Set<string>(
    NAVIGATION_CONFIG.flatMap((group) => group.modules.flatMap((module) => module.submoduleTypes)),
  );

  return {
    groups,
    ungroupedModules: buildUngroupedModules(
      submodules.filter((submodule) => !groupedTypes.has(submodule.type)),
    ),
  };
};

// Modified and middle clicks are left to the browser so sidebar links can open in a new tab
export const isNewTabClick = (event: MouseEvent): boolean =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

export const isNavSubmoduleActive = (
  submodule: NavSubmodule,
  isCurrentRoute: IsCurrentRoute,
): boolean => isCurrentRoute(submodule.match);

export const isNavModuleActive = (module: NavModule, isCurrentRoute: IsCurrentRoute): boolean =>
  module.submodules.some((submodule) => isNavSubmoduleActive(submodule, isCurrentRoute));

export const isNavGroupActive = (group: NavGroup, isCurrentRoute: IsCurrentRoute): boolean =>
  group.modules.some((module) => isNavModuleActive(module, isCurrentRoute));
